import React, { useState, useEffect } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import book1 from '../images/scoc.png';
import book2 from '../images/book2.png';
import book3 from '../images/book3.png';

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const bookCovers = [book1, book2, book3];

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-cream-50">
      {/* Elegant Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231a1a2e' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      {/* Decorative Ink Blots */}
      <div
        className="absolute top-20 left-10 w-96 h-96 rounded-full blur-3xl opacity-[0.03] bg-ink-900 animate-pulse-soft"
        style={{ transform: `translate(${mousePosition.x * 0.5}px, ${mousePosition.y * 0.5}px)` }}
      />
      <div
        className="absolute bottom-20 right-10 w-[500px] h-[500px] rounded-full blur-3xl opacity-[0.04] bg-gold-500 animate-pulse-soft"
        style={{ animationDelay: "1.5s", transform: `translate(${mousePosition.x * -0.3}px, ${mousePosition.y * -0.3}px)` }}
      />

      {/* Gold Accent Lines */}
      <div className="absolute top-0 left-1/4 w-px h-40 bg-gradient-to-b from-transparent via-gold-400 to-transparent opacity-30" />
      <div className="absolute bottom-0 right-1/3 w-px h-60 bg-gradient-to-t from-transparent via-gold-400 to-transparent opacity-30" />

      {/* Content */}
      <div className="container mx-auto px-6 lg:px-12 pt-32 pb-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="max-w-2xl">
            {/* Badge */}
            <div
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ink-900/5 border border-ink-200 mb-8 transform transition-all duration-700 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
            >
              <Sparkles className="w-4 h-4 text-gold-500" />
              <span className="text-sm font-sans tracking-wide text-ink-700">
                Bespoke Author Websites
              </span>
            </div>

            {/* Main Headline */}
            <h1
              className={`font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] mb-8 transform transition-all duration-700 delay-100 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              <span className="block text-ink-900">Where Words</span>
              <span className="block mt-2">
                <span className="text-ink-900">Find Their </span>
                <span className="relative">
                  <span className="text-gradient-gold">Home</span>
                  <svg
                    className="absolute -bottom-2 left-0 w-full"
                    viewBox="0 0 200 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2 8C50 2 100 2 198 8"
                      stroke="#c9a227"
                      strokeWidth="3"
                      strokeLinecap="round"
                      className={`transition-all duration-1000 delay-700 ${
                        isVisible ? "opacity-100" : "opacity-0"
                      }`}
                      style={{
                        strokeDasharray: 200,
                        strokeDashoffset: isVisible ? 0 : 200,
                        transition: "stroke-dashoffset 1s ease-out 0.7s",
                      }}
                    />
                  </svg>
                </span>
              </span>
            </h1>

            {/* Subheadline */}
            <p
              className={`font-body text-xl md:text-2xl text-ink-600 leading-relaxed mb-10 max-w-xl transform transition-all duration-700 delay-200 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              We craft{" "}
              <span className="text-ink-800 font-semibold">elegant digital spaces</span>{" "}
              for authors — where your stories captivate, your voice resonates, and readers become devoted fans.
            </p>

            {/* CTA Buttons */}
            <div
              className={`flex flex-col sm:flex-row gap-4 transform transition-all duration-700 delay-300 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              <button
                onClick={() => scrollToSection("pricing")}
                className="btn-primary group"
              >
                <span className="flex items-center gap-3">
                  View Packages
                  <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </button>
              <button
                onClick={() => scrollToSection("preview")}
                className="btn-secondary group"
              >
                <span className="flex items-center gap-3">
                  See Our Work
                  <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </button>
            </div>

            {/* Trust Indicators */}
            <div
              className={`mt-12 pt-8 border-t border-cream-300 transform transition-all duration-700 delay-400 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              <div className="flex items-center gap-8">
                <div>
                  <div className="font-display text-3xl font-bold text-ink-900">150+</div>
                  <div className="text-sm font-sans text-ink-500 tracking-wide">Authors Served</div>
                </div>
                <div className="w-px h-12 bg-cream-300" />
                <div>
                  <div className="font-display text-3xl font-bold text-ink-900">98%</div>
                  <div className="text-sm font-sans text-ink-500 tracking-wide">Satisfaction Rate</div>
                </div>
                <div className="w-px h-12 bg-cream-300" />
                <div>
                  <div className="font-display text-3xl font-bold text-ink-900">5★</div>
                  <div className="text-sm font-sans text-ink-500 tracking-wide">Average Rating</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Book Display */}
          <div
            className={`relative hidden lg:block transform transition-all duration-1000 delay-500 ${
              isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"
            }`}
          >
            {/* Decorative Frame */}
            <div className="absolute -inset-8 border border-gold-300/30 rounded-sm" />
            <div className="absolute -inset-4 border border-cream-300/50 rounded-sm" />

            {/* Books Container */}
            <div className="relative h-[600px] flex items-center justify-center perspective-1000">
              {/* Background Glow */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-80 h-80 rounded-full bg-gradient-radial from-gold-200/30 via-gold-100/10 to-transparent blur-2xl" />
              </div>

              {/* Floating Books */}
              {bookCovers.map((cover, index) => {
                const positions = [
                  { x: -80, y: -40, rotate: -12, z: 1 },
                  { x: 0, y: 0, rotate: 0, z: 3 },
                  { x: 80, y: -40, rotate: 12, z: 2 },
                ];
                const pos = positions[index];
                const isHovered = hoveredIndex === index;
                const isCenter = index === 1;

                return (
                  <div
                    key={index}
                    className="absolute transition-all duration-500 ease-luxury cursor-pointer"
                    style={{
                      transform: `
                        translateX(${pos.x + (isHovered ? 0 : 0)}px)
                        translateY(${pos.y + (isHovered ? -20 : 0)}px)
                        rotate(${isHovered ? 0 : pos.rotate}deg)
                        scale(${isHovered ? 1.1 : isCenter ? 1.05 : 0.95})
                      `,
                      zIndex: isHovered ? 10 : pos.z,
                    }}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    {/* Book Shadow */}
                    <div
                      className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-4/5 h-8 bg-ink-900/20 blur-xl rounded-full transition-all duration-500"
                      style={{
                        opacity: isHovered ? 0.4 : 0.2,
                        transform: `translateX(-50%) scale(${isHovered ? 1.2 : 1})`,
                      }}
                    />

                    {/* Book Container */}
                    <div className="relative group">
                      {/* Book Spine Effect */}
                      <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-ink-900/30 to-transparent rounded-l-sm" />

                      {/* Book Cover */}
                      <img
                        src={cover}
                        alt={`Featured Book ${index + 1}`}
                        className={`w-52 h-auto rounded-sm transition-all duration-500 ${
                          isHovered ? "shadow-luxury-lg" : "shadow-book"
                        }`}
                        style={{
                          filter: isHovered ? "brightness(1.05)" : "brightness(1)",
                        }}
                      />

                      {/* Hover Overlay */}
                      <div
                        className={`absolute inset-0 bg-gradient-to-t from-ink-900/60 via-transparent to-transparent rounded-sm transition-opacity duration-300 flex items-end justify-center pb-6 ${
                          isHovered ? "opacity-100" : "opacity-0"
                        }`}
                      >
                        <span className="text-cream-50 font-sans text-sm tracking-wide">
                          View Details
                        </span>
                      </div>

                      {/* Gold Corner Accent */}
                      <div className="absolute top-0 right-0 w-8 h-8 overflow-hidden">
                        <div className={`absolute top-0 right-0 w-12 h-12 bg-gradient-to-bl from-gold-400 to-transparent transform rotate-45 translate-x-6 -translate-y-6 transition-opacity duration-300 ${isHovered ? "opacity-100" : "opacity-0"}`} />
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Floating Decorative Elements */}
              <div className="absolute top-10 right-10 w-2 h-2 bg-gold-400 rounded-full animate-pulse-soft" />
              <div className="absolute bottom-20 left-20 w-3 h-3 bg-gold-300 rounded-full animate-pulse-soft" style={{ animationDelay: "1s" }} />
              <div className="absolute top-1/3 left-0 w-1.5 h-1.5 bg-gold-500 rounded-full animate-pulse-soft" style={{ animationDelay: "0.5s" }} />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-fade-in" style={{ animationDelay: "1.5s" }}>
        <span className="text-xs font-sans tracking-widest uppercase text-ink-400">Scroll to explore</span>
        <div className="w-px h-8 bg-gradient-to-b from-ink-300 to-transparent" />
      </div>
    </section>
  );
};

export default HeroSection;
